import menuTemplate from '../menu.html';
import menuItemTemplate from '../menuItem.html';
import flashIcon from '../assets/icons/flashplayer_16.png';
import '../menu.css';

/**
 * Using webpacks require.context we can dynamically import all PNG files from the icons folder.
 * This is useful because it allows us to add new icons to the application without having to
 * manually import them into the application.
 */
const iconsContext = require.context('../assets/icons', false, /\.png$/);
const icons = iconsContext.keys().reduce((icons, key) => {
  /**
   * In the HTML the item.icon we are expecting are formatted like so: 'iconName.png'.
   * Therefore, we want to make sure we only remove what we need to get the exact key
   * name we are looking for.
   */
  const iconName = key.replace('./', '');
  icons[iconName] = iconsContext(key);
  return icons;
}, {});

/**
 * Books application common page menu. Retrieves menu data from server and displays it
 * along with org logo & name and a greeting to the user.
 *
 * @module    menu
 * @ngdoc     component
 * @name      booksMenu
 */
var DialogServices = {
  'Change Login': 'ChangeLoginDialog',
  'Change Profile': 'ChangeProfileDialog',
  Logout: 'ConfirmLogoutDialog'
};

// TODO: GR-329 - Fix the whole icon exports
// var CONSTANTS = {
//   FP_ICONS: icons,
//   FP_ICON_FLASH: flashIcon,
//   TP_MENU: menuTemplate,
//   TP_MENU_ITEM_TREE: menuItemTemplate

/* @ngInject */ export function MenuItemTemplateCache($templateCache) {
  $templateCache.put('menu-item-template', menuItemTemplate);
}

export const booksMenuComponent = {
  template: menuTemplate,
  controller: controller
};

/***************************************************************************
 * Controller
 ***************************************************************************/
/* @ngInject */ function controller(
  $injector,
  $rootScope,
  $q,
  $scope,
  BooksEvents,
  CommonSvc,
  MenuService,
  SERVER,
  Session,
  TemplateTextBuilder
) {
  var $ctrl = this;
  var local = {};
  var $svcs = { $ctrl: $ctrl, local: local };

  $ctrl.CONSTANTS = {
    FP_ICONS: icons,
    FP_ICON_FLASH: flashIcon,
    TP_MENU_ITEM_TREE: 'menu-item-template'
  };
  $ctrl.currentUser = SERVER.currentUser;
  $ctrl.allowMouseover = false;
  $ctrl.activeItems = [];
  $ctrl.currentAppName = '';
  $ctrl.menuDisabled = false;
  $ctrl.hasFlash = MenuService.isFlashEnabled();

  $ctrl.activateApp = $injector.invoke(activateApp, null, $svcs);
  $ctrl.allowMouseoverMenu = $injector.invoke(
    allowMouseoverMenu,
    null,
    $svcs
  );
  $ctrl.endMenu = $injector.invoke(endMenu, null, $svcs);
  $ctrl.greetUser = $injector.invoke(greetUser, null, $svcs);
  $ctrl.isActiveMenu = $injector.invoke(isActiveMenu, null, $svcs);
  $ctrl.launch = $injector.invoke(launch, null, $svcs);
  $ctrl.menuItemMouseover = $injector.invoke(menuItemMouseover, null, $svcs);
  $ctrl.onBannerClick = $injector.invoke(onBannerClick, null, $svcs);
  $ctrl.allowMouseoverMenu = $injector.invoke(
    allowMouseoverMenu,
    null,
    $svcs
  );

  local.displayMenuItem = $injector.invoke(displayMenuItem, null, $svcs);
  local.updateActiveMenus = $injector.invoke(updateActiveMenus, null, $svcs);

  function createUserMenu(menu) {
    $ctrl.menu = makeMenu(menu, 0, 0);

    /*-----------------------------------------*/
    /* Find the app menu item by ID or by URL. */
    /*-----------------------------------------*/
    var menuID = MenuService.getAppMenuID();
    var menuItem = menuID
      ? findMenuItem($ctrl.menu, menuID, findMenuItem.matchID)
      : findMenuItem($ctrl.menu, Session.urlPathName, findMenuItem.matchURL);

    $ctrl.currentAppName =
      menuItem && menuItem.title != 'Welcome' ? menuItem.title : '';
    var local = window.location.hostname.includes('.loc');
    var staging = window.location.hostname.includes('.graystepstaging');

    $ctrl.serverLocation = local ? 'LOCAL' : staging ? 'STAGING' : null;
  }

  function createStorageKey() {
    /***
     * We added the orgID so that way if the user switches accounts they will still get the correct user menu, since the menu is based on what
     * organization you are logged into. Before I used local storage and added the session ID in as well,
     * but I learned the browser recognizes our sessions properly (even with multiple different sessions open on the same browser).
     * This made it so I did not have to get the session id and use as part of sessionStorageKey.
     * **/
    return `menu-time:${Date.now()}-orgID:${SERVER.currentUser.orgID}`;
  }

  /***
   * This checks to see if the user already has a menu within session storage if they do and it was retrieved at least within the last 3 hours
   * we use that menu otherwise we return null to let the handler know to retrieve a brand new menu.
   * **/
  function getUserMenu() {
    var menuKey = Object.keys(sessionStorage).find(
      (key) =>
        key.includes('menu-') &&
        key.includes(`orgID:${SERVER.currentUser.orgID}`)
    );

    if (!menuKey) return null;

    var menuCreationTimeStamp = menuKey.match(/time:(\d*)/);

    // position 1 of the array is the capture group which will be the time-stamp of the menu creation.
    var creationTime = menuCreationTimeStamp
      ? Number(menuCreationTimeStamp[1])
      : 0;

    var threeHours = 1000 * 60 * 60 * 3;
    // If creation time occurred less than 3 hours ago, its valid. Otherwise re-query. 3 hours matches our back-end server timeout -> 3 hours.
    if (creationTime > Date.now() - threeHours) {
      return JSON.parse(sessionStorage.getItem(menuKey));
    } else {
      /***
       * Here we clear out the original session storage item that is too old so that we do not use up browser memory for no reason.
       * **/
      sessionStorage.removeItem(menuKey);
      // return something to indicate that we need to fetch the menu again
      return null;
    }
  }

  function setupMenuInSessionStorage(menu) {
    /***
     * Since the menu is an array of menu items we can display we must stringify so we can read it later,
     * otherwise it would all just be [Object] [Object] in local storage.
     * **/
    sessionStorage.setItem(createStorageKey(), JSON.stringify(menu));

    // return the menu because we expect this to be followed up by createUserMenu
    return menu;
  }

  // We are expecting the menu to be a json.stringify array, so we must parse the results so we can process them properly.
  var menu = getUserMenu();

  /***
   * This was setup to make it so we do not waste db connections and processing time by querying for the same menu information on every page drop.
   * Now we store the menu in local storage if it does not exist yet, if it does we get it and create the menu from the pre-existing data.
   * The only downside is that if permissions change while someone is logged in then they will not experience that until they logout and back in.
   * IMO the benefits of reduced db connections + faster processing for the user out weigh the idea of stale data here. Sky T. (12/28/22)
   * **/
  if (menu && menu.length) createUserMenu(menu);
  else
    CommonSvc.getMenu()
      .then((res) => setupMenuInSessionStorage(res.data))
      .then(createUserMenu);

  /*----------------------*/
  /* Set the menu banner. */
  /*----------------------*/
  if (SERVER.BooksWebConstants.URL_BANNER_CONTENT)
    $ctrl.banner = SERVER.BooksWebConstants.URL_BANNER_CONTENT;

  /*---------------------------------------------------*/
  /* Set custom logo and add LOGO_CHANGED event to     */
  /* reload if a new logo is uploaded in school admin. */
  /*---------------------------------------------------*/
  var logo = SERVER.userImages[SERVER.BooksDBConstants.UIT_LOGO];
  var logoSrc = new TemplateTextBuilder(logo.path, {
    orgID: SERVER.currentUser.orgID
  }).toString();
  $ctrl.logo = {
    height: logo.height,
    width: logo.width,
    src: logoSrc + '?' + SERVER.logoTimestamp,
    altSrc: SERVER.BooksWebConstants.URL_PRODUCT_LOGO
  };

  $scope.$on(BooksEvents.LOGO_CHANGED, function () {
    $ctrl.logo.src = logoSrc + '?' + new Date().getTime();
  });

  /*----------------------------------------------------------------*/
  /* The menu can be included in an app with the FileImport module. */
  /* The menu needs to be disabled while the import is processing.  */
  /*----------------------------------------------------------------*/
  $rootScope.$on('fileImportStart', function () {
    $ctrl.menuDisabled = true;
    $ctrl.endMenu();
  });
  $rootScope.$on('fileImportEnd', function () {
    $ctrl.menuDisabled = false;
  });
}

/***************************************************************************
 * $ctrl.activateApp */
/**
 * Displays the user's APP/WIN selection and closes all menus
 *
 * @param  item       A single menu item object with a url to be displayed
 ***************************************************************************/
/* @ngInject */ function activateApp($ctrl, $q, MenuService, Alert) {
  return function (item) {
    if ($ctrl.menuDisabled) $ctrl.endMenu();

    var promise = !window.unsavedChanges
      ? $q.resolve()
      : Alert.confirm(
          'It appears there are unsaved changes in the current application. Would you like to continue without saving?',
          'Unsaved Changes'
        );

    promise.then(function () {
      MenuService.displayApp(item);
      $ctrl.endMenu();
    });
  };
}

/***************************************************************************
 * $ctrl.allowMouseoverMenu */
/**
 * When a top level menu item is clicked, this will toggle the abilty
 * to view any menu item's contents on mouseover as long as the menu is not
 * disabled.
 *
 * @param  i       Top level menu item to display or hide
 ***************************************************************************/
/* @ngInject */ function allowMouseoverMenu($ctrl) {
  return function (i) {
    $ctrl.allowMouseover = !$ctrl.allowMouseover && !$ctrl.menuDisabled;
    $ctrl.menuItemMouseover(i);
  };
}

/***************************************************************************
 * $ctrl.endMenu */
/**
 * Called when the mouse leaves the parent menu div.
 * Closes all menus and disables the dropdown during mouseover
 ***************************************************************************/
/* @ngInject */ function endMenu($ctrl) {
  return function () {
    $ctrl.activeItems = [];
    $ctrl.allowMouseover = false;
  };
}

/***************************************************************************
 * $ctrl.greetUser */
/**
 * Returns a personalized greeting with time of day and the
 * user account's first name to be displayed to the user.
 ***************************************************************************/
/* @ngInject */ function greetUser() {
  return function () {
    var today = new Date();
    var hour = today.getHours();
    var time;

    if (hour < 12) time = 'Morning';
    else if (hour < 18) time = 'Afternoon';
    else time = 'Evening';

    return 'Good ' + time + ', ';
  };
}

/***************************************************************************
 * $ctrl.isActiveMenu */
/**
 * If the menu item is found in the list of menus to display, return true. This
 * is used in ng-class to determine what items should have the css 'open' class
 *
 * @param  item        A single menu item object with a url to search for
 ***************************************************************************/
/* @ngInject */ function isActiveMenu($ctrl) {
  return function (item) {
    for (var i = 0; i < $ctrl.activeItems.length; i++) {
      if ($ctrl.activeItems[i] === item) return true;
    }
  };
}

/***************************************************************************
 * $ctrl.launch */
/**
 * Click event handler for popup/nav menu items
 *
 * @param  item The menu item of type 'POP' or 'NAV' to be opened
 ***************************************************************************/
/* @ngInject */ function launch($ctrl, $uibModal, $injector) {
  return function (item) {
    var service = DialogServices[item.title];
    if (service) $injector.get(service).show();
  };
}

/***************************************************************************
 * $ctrl.menuItemMouseover */
/**
 * If the user has clicked on a parent menu item, this displays it's contents
 * on mouseover
 *
 * @param  item       Top level menu item to display/hide
 ***************************************************************************/
/* @ngInject */ function menuItemMouseover($ctrl, local) {
  return function (item) {
    if ($ctrl.allowMouseover) local.displayMenuItem(item);
    else $ctrl.activeItems = [];
  };
}

/*****************************************************************************
 * $ctrl.onBannerClick */
/**
 * Banner mouse click handler. Prompts user with an alert before opening
 * the url in a new window.
 *
 * @param url          URL to navigate to
 * @param skipConfirm  Skips confirmation of external redirect message
 *****************************************************************************/
/* @ngInject */ function onBannerClick($q, Alert) {
  return function (url, skipConfirm) {
    (skipConfirm
      ? $q.resolve()
      : Alert.confirm(
          'You are being redirected to an external website hosted by Graystep Software.',
          'Please Confirm'
        )
    ).then(function () {
      window.open(url, '_blank');
    });
  };
}

/***************************************************************************
 * local.displayMenuItem */
/**
 * Maintain and update a list of menus/submenus to be kept open.
 * The list is kept in [parent, child, grandchild] (if any) order
 *
 * @param  item       Menu or submenu item to display
 ***************************************************************************/
/* @ngInject */ function displayMenuItem($ctrl, local) {
  return function (item) {
    /*------------------------------------------------------------------*/
    /* No top level menu is currently open, display current selection   */
    /*------------------------------------------------------------------*/
    if ($ctrl.activeItems.length == 0) $ctrl.activeItems.push(item);
    else {
      /*---------------------------------*/
      /* Sub menuitem clicked, display.  */
      /*---------------------------------*/
      if (item.parentID !== 0) {
        if (
          $ctrl.activeItems[$ctrl.activeItems.length - 1].menuID ===
          item.parentID
        )
          $ctrl.activeItems.push(item);
        else if (
          $ctrl.activeItems[$ctrl.activeItems.length - 1].parentID !==
          item.menuID
        ) {
          local.updateActiveMenus(item.parentID);
          $ctrl.activeItems.push(item);
        } else local.updateActiveMenus(item.parentID);
      } else if (item !== $ctrl.activeItems[0]) {
        $ctrl.activeItems = [];
        $ctrl.activeItems.push(item);
      }
    }
  };
}

/***************************************************************************
 * local.updateActiveMenus */
/**
 * using menuID, remove erything after the menu from list of items currently
 * being displayed
 *
 * @param  id       menuID of the item
 ***************************************************************************/
/* @ngInject */ function updateActiveMenus($ctrl) {
  return function (id) {
    for (var i = 0; i < $ctrl.activeItems.length; i++) {
      if ($ctrl.activeItems[i].menuID == id)
        $ctrl.activeItems.splice(i + 1, $ctrl.activeItems.length);
    }
  };
}

/*****************************************************************************
 * findMenuItem */
/**
 * Searches the menu item tree to find a menu item. `value` is passed to
 * `isMatch` for each item in `menu` and the item is returned if `isMatch`
 * returns true. This method is called recursively through for item submenus.
 *
 * @param  menu   Menu/Submenu to search.
 * @param  value  parentID   Parent menu ID.
 * @return            Menu items for parent ID.
 *****************************************************************************/
function findMenuItem(menu, value, isMatch) {
  var menuItem;

  menu.some(function (item) {
    return (menuItem = isMatch(item, value)
      ? item
      : item.submenu
        ? findMenuItem(item.submenu, value, isMatch)
        : null);
  });

  return menuItem;
}

/*----------------------------------------*/
/* Delegates to match current app against */
/* menu options by Menu ID and App URL.   */
/*----------------------------------------*/
findMenuItem.matchID = function (item, value) {
  return value == item.menuID;
};
findMenuItem.matchURL = function (item, value) {
  return item.url && value == item.url.split('?').shift();
};

/*****************************************************************************
 * makeMenu */
/**
 * Converts the linear list of menu items read from the server into the menu
 * item tree. List from server must in display order. This method is called
 * recursively.
 *
 * @param  menuItems  Linear menu item list from server.
 * @param  parentID   Parent menu ID, 0=top of tree.
 * @return            Menu items for `parentID`, null=none.
 *****************************************************************************/
function makeMenu(menuItems, parentID) {
  var menu = menuItems.filter(function (item) {
    return item.parentID == parentID;
  });

  menu.forEach(function (parent) {
    parent.submenu = makeMenu(menuItems, parent.menuID);
  });

  return menu.length ? menu : null;
}
